import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	ResizablePanelGroup,
	ResizablePanel,
	ResizableHandle,
} from "@/components/ui/resizable";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ArrowLeft,
	Loader2,
	CheckSquare,
	Square,
	Route,
	Clock,
	UserCheck,
	Layers,
} from "lucide-react";
import axios from "@/lib/axios";
import { adminService } from "@/pagesadmin/adminApi";
import { useToast } from "@/hooks/use-toast";

// @origin: v2 | @since: 3d9ebf6d, ac6ee83f | @used-by: ALL | @purpose: 2f port
// -- per-user chronological URL/time-spent drill-down, reached via
// JourneyAnalysis.tsx's new "URL / Time Analysis" row action. Ported
// verbatim from donor tip (5fe70ff4); backend counterpart is
// los_app.apis.admin_user_journey_events.

interface UserOption {
	id: number;
	first_name: string;
	last_name: string;
	business_name: string | null;
}

interface JourneyEventItem {
	id: number;
	route: string;
	stage: string | null;
	event_type: string;
	created_at: string;
	time_spent_seconds: number | null;
	time_spent_display: string;
}

interface UserJourneyEventsResponse {
	user: {
		id: number;
		full_name: string;
		business_name: string | null;
	};
	events: JourneyEventItem[];
	unique_routes: string[];
}

// Human-readable labels for known journey stage keys.
// Falls back to the raw key (title-cased) for any unknown stage.
const STAGE_LABELS: Record<string, string> = {
	loan_application:      "Loan Application",
	business_information:  "Business Information",
	lender_offers:         "Lender Offers",
	personal_details:      "Personal Details",
	bank_details:          "Bank Details",
	kyc:                   "KYC",
	selfie:                "Selfie",
	aa_consent:            "AA Consent",
	e_sign:                "E-Sign",
	completed:             "Completed",
	rejected:              "Rejected",
};

// Colour palette — cycles through these for unknown stages
const STAGE_COLOURS = [
	"bg-blue-100 text-blue-700 border-blue-200",
	"bg-purple-100 text-purple-700 border-purple-200",
	"bg-amber-100 text-amber-700 border-amber-200",
	"bg-rose-100 text-rose-700 border-rose-200",
	"bg-teal-100 text-teal-700 border-teal-200",
	"bg-indigo-100 text-indigo-700 border-indigo-200",
	"bg-orange-100 text-orange-700 border-orange-200",
	"bg-cyan-100 text-cyan-700 border-cyan-200",
];

const STAGE_FIXED_COLOURS: Record<string, string> = {
	loan_application:     "bg-green-100 text-green-700 border-green-200",
	business_information: "bg-blue-100 text-blue-700 border-blue-200",
	lender_offers:        "bg-purple-100 text-purple-700 border-purple-200",
	personal_details:     "bg-amber-100 text-amber-700 border-amber-200",
	bank_details:         "bg-teal-100 text-teal-700 border-teal-200",
	kyc:                  "bg-indigo-100 text-indigo-700 border-indigo-200",
	selfie:               "bg-pink-100 text-pink-700 border-pink-200",
	aa_consent:           "bg-orange-100 text-orange-700 border-orange-200",
	e_sign:               "bg-cyan-100 text-cyan-700 border-cyan-200",
	completed:            "bg-emerald-100 text-emerald-700 border-emerald-200",
	rejected:             "bg-red-100 text-red-700 border-red-200",
};

const getStageBadgeClass = (stage: string | null, dynamicIndex: number): string => {
	if (!stage) return "bg-gray-100 text-gray-500 border-gray-200";
	return STAGE_FIXED_COLOURS[stage] ?? STAGE_COLOURS[dynamicIndex % STAGE_COLOURS.length];
};

const getStageLabel = (stage: string | null): string => {
	if (!stage) return "—";
	return STAGE_LABELS[stage] ?? stage.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

interface JourneyUrlTimeAnalysisProps {
	initialUserId: number | null;
	onBack: () => void;
}

const formatDate = (iso: string) => {
	const d = new Date(iso);
	if (isNaN(d.getTime())) return "-";
	return d.toLocaleString("en-IN", {
		timeZone: "Asia/Kolkata",
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
};

const formatDurationSeconds = (seconds: number | null): string => {
	if (seconds === null || seconds === undefined) return "—";
	if (seconds < 0) seconds = 0;
	const s = Math.round(seconds);
	if (s < 60) return `${s}s`;
	const minutes = Math.floor(s / 60);
	const secs = s % 60;
	if (minutes < 60) return secs ? `${minutes}m ${secs}s` : `${minutes}m`;
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const JourneyUrlTimeAnalysis: React.FC<JourneyUrlTimeAnalysisProps> = ({ initialUserId, onBack }) => {
	const { toast } = useToast();

	const [users, setUsers] = useState<UserOption[]>([]);
	const [usersLoading, setUsersLoading] = useState(true);
	const [activeUserId, setActiveUserId] = useState<number | null>(initialUserId);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<UserJourneyEventsResponse | null>(null);

	const [selectedRoutes, setSelectedRoutes] = useState<Set<string>>(new Set());
	const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());

	// Fetch users for the top dropdown
	useEffect(() => {
		const fetchUsers = async () => {
			setUsersLoading(true);
			try {
				const res = await axios.post("/api/public/users/");
				setUsers(res.data as UserOption[]);
			} catch (err) {
				console.error("Failed to load users", err);
			} finally {
				setUsersLoading(false);
			}
		};
		fetchUsers();
	}, []);

	// Fetch journey events whenever activeUserId changes
	useEffect(() => {
		if (!activeUserId) {
			setData(null);
			return;
		}
		const fetchEvents = async () => {
			setLoading(true);
			setError(null);
			try {
				const res = await adminService.getUserJourneyEvents(activeUserId);
				const payload: UserJourneyEventsResponse = res.data;
					setData(payload);
					// Default: all routes selected (no filtering)
					setSelectedRoutes(new Set(payload.unique_routes));
					// Default: all stages selected
					const stagesInPayload = Array.from(
						new Set(payload.events.map(e => e.stage ?? "__none__"))
					);
					setSelectedStages(new Set(stagesInPayload));
			} catch (err: unknown) {
				console.error(err);
				const msg =
					(err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.detail ||
					(err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.error ||
					"Failed to load journey events";
				setError(msg);
				toast({ title: "Error", description: msg, variant: "destructive" });
			} finally {
				setLoading(false);
			}
		};
		fetchEvents();
	}, [activeUserId, toast]);

	const handleUserSelect = (val: string) => {
		setActiveUserId(Number(val));
	};

	const toggleRoute = (route: string) => {
		setSelectedRoutes((prev) => {
			const next = new Set(prev);
			if (next.has(route)) next.delete(route);
			else next.add(route);
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (!data) return;
		setSelectedRoutes((prev) => {
			if (prev.size === data.unique_routes.length) {
				return new Set();
			}
			return new Set(data.unique_routes);
		});
	};

	const toggleStage = (stage: string) => {
		setSelectedStages((prev) => {
			const next = new Set(prev);
			if (next.has(stage)) next.delete(stage);
			else next.add(stage);
			return next;
		});
	};

	const toggleSelectAllStages = (uniqueStages: string[]) => {
		setSelectedStages((prev) => {
			if (prev.size === uniqueStages.length) return new Set();
			return new Set(uniqueStages);
		});
	};

	// Derive unique stages from all events (client-side — backend doesn't send this list)
	const uniqueStages = useMemo(() => {
		if (!data) return [];
		const seen = new Map<string, number>(); // stage key → first-seen order index
		data.events.forEach(e => {
			const key = e.stage ?? "__none__";
			if (!seen.has(key)) seen.set(key, seen.size);
		});
		return Array.from(seen.keys());
	}, [data]);

	// Colour index map so each stage gets a consistent badge colour across the table
	const stageColourIndex = useMemo(() => {
		const map = new Map<string, number>();
		uniqueStages.forEach((s, i) => map.set(s, i));
		return map;
	}, [uniqueStages]);

	// Filtered + re-computed time-until-next based on the CURRENTLY SELECTED routes AND stages.
	// This must be recomputed client-side because removing intermediate events changes
	// the gap between the remaining consecutive events.
	const filteredEvents = useMemo(() => {
		if (!data) return [];
		const filtered = data.events.filter(
			(e) => selectedRoutes.has(e.route) && selectedStages.has(e.stage ?? "__none__")
		);
		return filtered.map((event, idx) => {
			const nextEvent = filtered[idx + 1];
			let displayTime = "—";
			let seconds: number | null = null;
			if (nextEvent) {
				const delta = (new Date(nextEvent.created_at).getTime() - new Date(event.created_at).getTime()) / 1000;
				if (delta >= 0) {
					seconds = delta;
					displayTime = formatDurationSeconds(delta);
				}
			}
			return { ...event, computed_time_display: displayTime, computed_time_seconds: seconds };
			});
		}, [data, selectedRoutes, selectedStages]);

	const sessionSpan = useMemo(() => {
		if (filteredEvents.length < 2) return null;
		const first = new Date(filteredEvents[0].created_at).getTime();
		const last = new Date(filteredEvents[filteredEvents.length - 1].created_at).getTime();
		return formatDurationSeconds((last - first) / 1000);
	}, [filteredEvents]);

	// Per-stage time totals (sum of computed_time_seconds for each stage)
	const stageTimeTotals = useMemo(() => {
		const totals = new Map<string, number>();
		filteredEvents.forEach(e => {
			if (e.computed_time_seconds !== null) {
				const key = e.stage ?? "__none__";
				totals.set(key, (totals.get(key) ?? 0) + e.computed_time_seconds);
			}
		});
		return totals;
	}, [filteredEvents]);

	const selectedUser = users.find((u) => u.id === activeUserId);
	const displayName = data?.user.full_name || (selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim() : "");
	const displayBusiness = data?.user.business_name || selectedUser?.business_name;

	return (
		<div className="space-y-4">
			{/* Header bar */}
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
				<Button
					variant="outline"
					size="sm"
					onClick={onBack}
					className="flex items-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm shrink-0"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to User List
				</Button>

				<div className="flex-1 min-w-0 flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
					<Select value={activeUserId ? String(activeUserId) : ""} onValueChange={handleUserSelect} disabled={usersLoading}>
						<SelectTrigger className="w-full sm:w-[380px] bg-white border-gray-200">
							<SelectValue placeholder={usersLoading ? "Loading users…" : "Select a customer…"} />
						</SelectTrigger>
						<SelectContent className="max-h-72">
							{users.map((u) => {
								const fullName = (u.first_name || u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : `User #${u.id}`;
								return (
									<SelectItem key={u.id} value={String(u.id)} className="text-sm">
										<span className="font-medium">{fullName}</span>
										{u.business_name && <span className="text-gray-400 ml-2">({u.business_name})</span>}
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>

					{displayName && (
						<div className="flex items-center gap-2 px-3 py-1.5 bg-intellend-green/5 rounded-full border border-intellend-green/20 shrink-0">
							<UserCheck className="h-4 w-4 text-intellend-green" />
							<span className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">{displayName}</span>
							{displayBusiness && <span className="text-xs text-gray-400 truncate max-w-[150px]">({displayBusiness})</span>}
						</div>
					)}
				</div>
			</div>

			{!activeUserId ? (
				<Card className="border-intellend-green/10 shadow-sm">
					<CardContent className="flex flex-col items-center justify-center py-20 text-gray-400">
						<Route className="h-10 w-10 mb-3 opacity-30" />
						<p className="text-sm font-medium">Select a customer from the dropdown above to view their URL / time analysis.</p>
					</CardContent>
				</Card>
			) : loading ? (
				<div className="flex items-center justify-center py-20">
					<Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
				</div>
			) : error ? (
				<div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">{error}</div>
			) : data && data.events.length === 0 ? (
				<Card className="border-intellend-green/10 shadow-sm">
					<CardContent className="flex flex-col items-center justify-center py-20 text-gray-400">
						<Clock className="h-10 w-10 mb-3 opacity-30" />
						<p className="text-sm font-medium">No page-view history for this user yet.</p>
					</CardContent>
				</Card>
			) : data ? (
				<>
					{/* Summary strip */}
					<div className="flex flex-wrap items-center gap-4 px-4 py-2.5 bg-gray-50/80 rounded-lg border border-gray-100 text-sm">
						<span className="text-gray-600">
							<span className="font-semibold text-gray-900">{filteredEvents.length}</span> visits shown
						</span>
						<div className="h-4 w-[1px] bg-gray-300" />
						<span className="text-gray-600">
							Total tracked: <span className="font-semibold text-gray-900">{data.events.length}</span> events
						</span>
						{sessionSpan && (
							<>
								<div className="h-4 w-[1px] bg-gray-300" />
								<span className="text-gray-600">
									Session span: <span className="font-semibold text-intellend-green">{sessionSpan}</span>
								</span>
							</>
						)}
					</div>

					{/* Stage time summary cards */}
					{stageTimeTotals.size > 0 && (
						<div className="flex flex-wrap gap-2 px-1">
							{uniqueStages
								.filter(s => stageTimeTotals.has(s))
								.map(s => (
									<div
										key={s}
										className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${getStageBadgeClass(s === "__none__" ? null : s, stageColourIndex.get(s) ?? 0)}`}
									>
										<Layers className="h-3 w-3 opacity-70" />
										<span>{s === "__none__" ? "No Stage" : getStageLabel(s)}</span>
										<span className="opacity-60 mx-0.5">·</span>
										<span className="font-mono">{formatDurationSeconds(stageTimeTotals.get(s) ?? 0)}</span>
									</div>
								))}
						</div>
					)}
	
					{/* Resizable split */}
					<Card className="border-intellend-green/10 shadow-sm overflow-hidden">
						<CardContent className="p-0">
							<ResizablePanelGroup direction="horizontal" className="min-h-[600px]">
								{/* LEFT: filter panels (URLs + Stages) */}
								<ResizablePanel defaultSize={28} minSize={20} maxSize={45}>
									<div className="h-full flex flex-col border-r border-gray-100 overflow-y-auto">
	
										{/* ── URL filter ── */}
										<div className="flex items-center justify-between p-4 border-b bg-gray-50/50 sticky top-0 z-10">
											<Label className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
												<Route className="h-4 w-4 text-intellend-green" />
												Filter URLs
											</Label>
											<Button
												variant="ghost"
												size="sm"
												onClick={toggleSelectAll}
												className="h-7 text-[11px] font-bold uppercase tracking-tighter text-intellend-green hover:bg-intellend-green/10"
											>
												{selectedRoutes.size === data.unique_routes.length ? (
													<><Square className="h-3 w-3 mr-1" /> Deselect All</>
												) : (
													<><CheckSquare className="h-3 w-3 mr-1" /> Select All</>
												)}
											</Button>
										</div>
										<div className="p-4 space-y-2.5 border-b border-gray-100">
											{data.unique_routes.length === 0 ? (
												<p className="text-xs text-gray-400 italic">No routes found.</p>
											) : (
												data.unique_routes.map((route) => (
													<div
														key={route}
														className="flex items-center space-x-3 group cursor-pointer"
														onClick={() => toggleRoute(route)}
													>
														<Checkbox
															id={`route-${route}`}
															checked={selectedRoutes.has(route)}
															className="data-[state=checked]:bg-intellend-green data-[state=checked]:border-intellend-green transition-all"
														/>
														<Label
															htmlFor={`route-${route}`}
															className="text-xs font-mono text-gray-600 group-hover:text-intellend-green transition-colors cursor-pointer break-all"
														>
															{route}
														</Label>
													</div>
												))
											)}
										</div>
	
										{/* ── Stage filter ── */}
										<div className="flex items-center justify-between p-4 border-b bg-gray-50/50 sticky top-0 z-10">
											<Label className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
												<Layers className="h-4 w-4 text-intellend-green" />
												Filter Stages
											</Label>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => toggleSelectAllStages(uniqueStages)}
												className="h-7 text-[11px] font-bold uppercase tracking-tighter text-intellend-green hover:bg-intellend-green/10"
											>
												{selectedStages.size === uniqueStages.length ? (
													<><Square className="h-3 w-3 mr-1" /> Deselect All</>
												) : (
													<><CheckSquare className="h-3 w-3 mr-1" /> Select All</>
												)}
											</Button>
										</div>
										<div className="p-4 space-y-2.5">
											{uniqueStages.length === 0 ? (
												<p className="text-xs text-gray-400 italic">No stages found.</p>
											) : (
												uniqueStages.map((stageKey) => {
													const colIdx = stageColourIndex.get(stageKey) ?? 0;
													const label = stageKey === "__none__" ? "No Stage" : getStageLabel(stageKey);
													const totalTime = stageTimeTotals.get(stageKey);
													return (
														<div
															key={stageKey}
															className="flex items-center space-x-3 group cursor-pointer"
															onClick={() => toggleStage(stageKey)}
														>
															<Checkbox
																id={`stage-${stageKey}`}
																checked={selectedStages.has(stageKey)}
																className="data-[state=checked]:bg-intellend-green data-[state=checked]:border-intellend-green transition-all shrink-0"
															/>
															<div className="flex flex-col min-w-0">
																<span
																	className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold w-fit ${getStageBadgeClass(stageKey === "__none__" ? null : stageKey, colIdx)}`}
																>
																	{label}
																</span>
																{totalTime !== undefined && (
																	<span className="text-[9px] text-gray-400 font-mono mt-0.5">
																		Total: {formatDurationSeconds(totalTime)}
																	</span>
																)}
															</div>
														</div>
													);
												})
											)}
										</div>
									</div>
								</ResizablePanel>
	
								<ResizableHandle withHandle />
	
								{/* RIGHT: chronological table */}
								<ResizablePanel defaultSize={72}>
									<div className="h-full overflow-y-auto">
										{(selectedRoutes.size === 0 || selectedStages.size === 0) ? (
											<div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
												<Square className="h-10 w-10 mb-3 opacity-30" />
												<p className="text-sm font-medium">
													{selectedRoutes.size === 0
														? "No URLs selected. Choose at least one URL on the left to see visits."
														: "No stages selected. Choose at least one stage on the left to see visits."}
												</p>
											</div>
										) : (
											<Table>
												<TableHeader className="sticky top-0 bg-white z-10">
													<TableRow className="bg-gray-50">
														<TableHead className="w-[50px] font-semibold text-gray-700">#</TableHead>
														<TableHead className="font-semibold text-gray-700">Stage</TableHead>
														<TableHead className="font-semibold text-gray-700">Route</TableHead>
														<TableHead className="font-semibold text-gray-700">Visited At (IST)</TableHead>
														<TableHead className="font-semibold text-gray-700 text-right">Time Until Next</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{filteredEvents.map((event, idx) => {
														const stageKey = event.stage ?? "__none__";
														const colIdx = stageColourIndex.get(stageKey) ?? 0;
														return (
															<TableRow key={event.id} className="hover:bg-intellend-green/5 transition-colors">
																<TableCell className="text-xs font-mono text-gray-400">{idx + 1}</TableCell>
																<TableCell className="whitespace-nowrap">
																	<span
																		className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStageBadgeClass(event.stage, colIdx)}`}
																	>
																		{getStageLabel(event.stage)}
																	</span>
																</TableCell>
																<TableCell className="font-mono text-xs text-gray-800">{event.route}</TableCell>
																<TableCell className="text-xs text-gray-600 whitespace-nowrap">{formatDate(event.created_at)}</TableCell>
																<TableCell className="text-right font-mono text-xs font-semibold text-intellend-green">
																	{event.computed_time_display}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										)}
									</div>
								</ResizablePanel>
							</ResizablePanelGroup>
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
};

export default JourneyUrlTimeAnalysis;
